import { z } from 'zod';
import { count, isoDate, isoTimestamp } from './primitives.js';
/*
 * GET /attribution — per-click ad identity from the UTM/ad_id-tagged landing
 * URLs (visitor_attribution), plus a per-ad rollup. PII-safe by design: the
 * handler never emits clientIp/userAgent, fbclid/fbp values, or PII hashes.
 *
 * ⚠️ `matched` / `matchedBookings` come from the HEURISTIC booking→visit
 * pairing (most-recent-unclaimed-fbclid fallback) and over-count real ad
 * bookings — Meta Ads Manager is the source of truth for ad credit.
 */
const ok = z.literal(true);
/** The exact ad behind a click, parsed from the tagged URL. All null until ads carry UTM params. */
export const attributionAdSchema = z.object({
    adName: z.string().nullable(), // utm_content = {{ad.name}}
    adId: z.string().nullable(), // ad_id = {{ad.id}}
    campaign: z.string().nullable(), // utm_campaign = {{campaign.name}}
    adset: z.string().nullable(), // utm_term = {{adset.name}}
});
/** One real ad click (eventType='visit' with an fbclid — bots never carry one). */
export const attributionRowSchema = z.object({
    id: z.number(),
    landedAt: isoTimestamp,
    landingPage: z.string().nullable(),
    ad: attributionAdSchema,
    hasAdIdentity: z.boolean(),
    /** CAPI event sent for this visit — heuristic match, over-counts (trust Meta). */
    matched: z.boolean(),
    matchedEventName: z.string().nullable(), // 'Lead' | 'Schedule'
    matchedAt: isoTimestamp.nullable(),
});
/** Rollup per ad (grouped by adId ?? adName ?? the unknown bucket). */
export const attributionByAdSchema = z.object({
    adName: z.string().nullable(),
    adId: z.string().nullable(),
    campaign: z.string().nullable(),
    adset: z.string().nullable(),
    clicks: count,
    /** Booking-modal opens (the dark-funnel beacon) carrying this ad's identity. */
    modalOpens: count,
    /** Heuristically matched bookings — over-counts; Meta Ads Manager is truth. */
    matchedBookings: count,
    lastClickAt: isoTimestamp.nullable(),
});
export const attributionResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    rows: z.array(attributionRowSchema),
    byAd: z.array(attributionByAdSchema),
});
//# sourceMappingURL=attribution.js.map